import { useState } from "react";
import { CONCEPT_MAX_SELECTION } from "./Config";
import "./ConceptUsageGuide.css";

interface ConceptUsageGuideProps {
  onOpenSearch: () => void;
}

interface GuideStep {
  key: string;
  tone: "pink" | "blue" | "violet";
  title: string;
  desc: string;
  icon: JSX.Element;
}

interface GuideFeature {
  key: string;
  tone: "blue" | "green" | "amber";
  tag: string;
  title: string;
  desc: string;
  icon: JSX.Element;
}

function SearchStepIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="10.5" cy="10.5" r="6.25" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M15.2 15.2L17.8 17.8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IntersectStepIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="9" cy="12" r="5.5" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="15" cy="12" r="5.5" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function ListStepIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 7h12M6 12h12M6 17h8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="18" cy="17" r="2" fill="currentColor" />
    </svg>
  );
}

function DragonIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 17l4-9 3 5 3-3 4 7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 17h14"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FundamentalIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="3.5" height="8" rx="1.2" fill="currentColor" opacity="0.35" />
      <rect x="10.25" y="8" width="3.5" height="11" rx="1.2" fill="currentColor" opacity="0.6" />
      <rect x="15.5" y="5" width="3.5" height="14" rx="1.2" fill="currentColor" />
    </svg>
  );
}

function PositionIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4v16M8 8l4-4 4 4M8 16l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const STEPS: GuideStep[] = [
  {
    key: "search",
    tone: "pink",
    title: "搜索概念",
    desc: "点右上角搜索，按名称、拼音或代码查找",
    icon: <SearchStepIcon />,
  },
  {
    key: "select",
    tone: "violet",
    title: "选取概念",
    desc: `展示同时属于指定概念的股票，最多选${CONCEPT_MAX_SELECTION}个概念`,
    icon: <IntersectStepIcon />,
  },
  {
    key: "browse",
    tone: "blue",
    title: "浏览与排序",
    desc: "查看行情指标，按表头切换排序或恢复潜龙排序",
    icon: <ListStepIcon />,
  },
];

const FEATURES: GuideFeature[] = [
  {
    key: "dragon",
    tone: "blue",
    tag: "默认排序",
    title: "潜龙排序",
    desc: "先按基本面分每 10 分一档分组（如 90～100 同档），档内再按位置分从高到低排列，优先展示质地好、位置优的标的。",
    icon: <DragonIcon />,
  },
  {
    key: "fundamental",
    tone: "green",
    tag: "核心指标",
    title: "基本面分",
    desc: "综合反映公司基本面质量，分值越高通常代表经营与财务质地越强，是潜龙排序的首要分层依据。",
    icon: <FundamentalIcon />,
  },
  {
    key: "position",
    tone: "amber",
    tag: "核心指标",
    title: "位置分",
    desc: "反映股价所处相对位置，辅助判断性价比与追高风险；在相同基本面档位内，位置分更高的股票会排在前面。",
    icon: <PositionIcon />,
  },
];

function GuideToggleChevron(props: { expanded: boolean }): JSX.Element {
  const { expanded } = props;
  return (
    <span
      className={
        expanded
          ? "lf-concept-guide__toggle-icon lf-concept-guide__toggle-icon--expanded"
          : "lf-concept-guide__toggle-icon"
      }
      aria-hidden
    >
      <svg viewBox="0 0 16 16" fill="none">
        <path
          d="M4 6l4 4 4-4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export default function ConceptUsageGuide(props: ConceptUsageGuideProps): JSX.Element {
  const { onOpenSearch } = props;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="lf-concept-guide">
      <button
        type="button"
        className="lf-concept-guide__toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="lf-concept-guide__toggle-label">概念库怎么用</span>
        <GuideToggleChevron expanded={expanded} />
      </button>

      {expanded ? (
        <>
          <section className="lf-concept-guide__steps" aria-label="使用步骤">
            {STEPS.map((step, index) => (
              <article
                key={step.key}
                className={`lf-concept-guide__step lf-concept-guide__step--${step.tone}`}
              >
                <span className="lf-concept-guide__step-index">{index + 1}</span>
                <div className="lf-concept-guide__step-icon">{step.icon}</div>
                <div className="lf-concept-guide__step-body">
                  <h3 className="lf-concept-guide__step-title">{step.title}</h3>
                  <p className="lf-concept-guide__step-desc">{step.desc}</p>
                </div>
              </article>
            ))}
          </section>

          <section className="lf-concept-guide__features" aria-label="指标说明">
            <h3 className="lf-concept-guide__section-label">重要指标</h3>
            <div className="lf-concept-guide__feature-grid">
              {FEATURES.map((feature) => (
                <article
                  key={feature.key}
                  className={`lf-concept-guide__feature lf-concept-guide__feature--${feature.tone}`}
                >
                  <div className="lf-concept-guide__feature-head">
                    <div className="lf-concept-guide__feature-leading">
                      <div className="lf-concept-guide__feature-icon">{feature.icon}</div>
                      <h4 className="lf-concept-guide__feature-title">{feature.title}</h4>
                    </div>
                    <span className="lf-concept-guide__feature-tag">{feature.tag}</span>
                  </div>
                  <p className="lf-concept-guide__feature-desc">{feature.desc}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="lf-concept-guide__tips">
            <p className="lf-concept-guide__tips-text">
              点击表头可按最新价、涨跌幅或指标列排序；点击指标数值可切换基本面分、位置分、股息率、市值等；点击股票名称可查看详情。
            </p>
          </section>

          <button type="button" className="lf-concept-guide__cta" onClick={onOpenSearch}>
            开始选择概念
          </button>
        </>
      ) : null}
    </div>
  );
}
